import { Icon } from "@/components/icons";
import { Button } from "@/components/button";
import { MonitoringHeader } from "./monitoring-header";

type ShellProps = {
  onNewMonitoring: () => void;
  onUploadBatch: () => void;
  onDownloadReport: () => void;
  canManageEntries?: boolean;
};

export function MonitoringLoading({
  onNewMonitoring,
  onUploadBatch,
  onDownloadReport,
  canManageEntries = true,
}: ShellProps) {
  return (
    <div className="flex min-h-screen flex-1 flex-col bg-grey-25 px-4 py-4 sm:px-8">
      <MonitoringHeader
        dim
        showUploadBatch
        showDownloadReport
        canManageEntries={canManageEntries}
        onNewMonitoring={onNewMonitoring}
        onUploadBatch={onUploadBatch}
        onDownloadReport={onDownloadReport}
      />
      <div className="mt-4 flex-1" aria-busy="true">
        <div className="flex animate-pulse flex-col gap-2" aria-hidden>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="h-9 w-full rounded-[4px] bg-grey-200 sm:w-[320px]" />
            <div className="h-9 w-full rounded-[4px] bg-grey-200 sm:w-[200px]" />
          </div>
          <div className="mt-2 h-9 w-full rounded-[4px] bg-grey-200" />
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-11 w-full rounded-[4px] bg-grey-100" />
          ))}
        </div>
        <span className="sr-only">Loading monitoring entries…</span>
      </div>
    </div>
  );
}

export function MonitoringProcessingBatch({
  onNewMonitoring,
  onUploadBatch,
  onDownloadReport,
  canManageEntries = true,
}: ShellProps) {
  return (
    <div className="flex min-h-screen flex-1 flex-col bg-grey-25 px-4 py-4 sm:px-8">
      <MonitoringHeader
        dim
        showUploadBatch
        showDownloadReport
        canManageEntries={canManageEntries}
        onNewMonitoring={onNewMonitoring}
        onUploadBatch={onUploadBatch}
        onDownloadReport={onDownloadReport}
      />
      <div
        className="mt-8 flex flex-1 flex-col items-center justify-center gap-3 rounded-[4px] border border-grey-200 bg-white px-4 py-12 text-center shadow-flat"
        aria-busy="true"
      >
        <div className="size-8 animate-spin rounded-full border-[3px] border-green-200 border-t-green-500" />
        <div className="flex flex-col gap-0.5">
          <p className="text-body font-medium text-black">Setting up your monitoring entries</p>
          <p className="text-small text-grey-900">
            This can take a few moments — they&rsquo;ll appear here automatically.
          </p>
        </div>
      </div>
    </div>
  );
}

type ErrorProps = ShellProps & {
  onRetry: () => void;
};

export function MonitoringError({
  onRetry,
  onNewMonitoring,
  onUploadBatch,
  onDownloadReport,
  canManageEntries = true,
}: ErrorProps) {
  return (
    <div className="flex min-h-screen flex-1 flex-col bg-grey-25 px-4 py-4 sm:px-8">
      <MonitoringHeader
        dim
        showUploadBatch
        showDownloadReport
        canManageEntries={canManageEntries}
        onNewMonitoring={onNewMonitoring}
        onUploadBatch={onUploadBatch}
        onDownloadReport={onDownloadReport}
      />
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-1.5 text-center">
          <Icon name="error" size={40} weight={300} className="text-grey-500" />
          <h2 className="text-h4 font-semibold text-black">
            Couldn&rsquo;t load monitoring entries
          </h2>
          <p className="w-[260px] text-small text-grey-900">
            Something went wrong while loading your entries. Please try again.
          </p>
        </div>
        <Button
          hierarchy="secondary"
          size="sm"
          leftIcon={<Icon name="refresh" size={20} weight={300} />}
          onClick={onRetry}
        >
          Try again
        </Button>
      </div>
    </div>
  );
}
